import type { CableTraySegment } from "../../utils/fetchCableTakeoffPaths";
import type { PlanTrays } from "./types";

/**
 * The trays THIS APP holds, as drawn lengths for the plan to render.
 *
 * The cables tab used to draw the agent's frozen copy (`bundle.tray`) while the
 * router reads `quantity_surveyor_routes`. Those are two different drawings the
 * moment anybody adds or renames a tray, and a recalculate would then move
 * metres over steel the tab does not show — which is exactly how a drawing
 * teaches an estimator not to trust it.
 *
 * `routeGroups` is already in memory, already per plan, already in PDF POINTS,
 * and it is the same table the server routes over. One source, no fetch, and it
 * updates as he draws.
 *
 * An edge whose endpoints are not both in `nodes` is dropped rather than
 * guessed: half a tray drawn to a point nobody published is worse than none.
 */
export const planTrays: PlanTrays = ({
  routeGroups,
  pageIndex,
  acceptedTypes,
}) => {
  const out: CableTraySegment[] = [];
  for (const group of routeGroups ?? []) {
    // Only real trays. `bundle.tray` was already filtered by the run's
    // classifier, so reading `routeGroups` raw put every drawn line back —
    // boundaries, core drills, the earth ring — both on screen and under the
    // router, which is how a cable came to be drawn across a floor.
    if (!acceptedTypes.has(group.label)) continue;
    for (const route of group.routes) {
      if (route.page !== pageIndex) continue;
      const byId = new Map(route.nodes.map((node) => [node.id, node]));
      for (const edge of route.edges) {
        const from = byId.get(edge.from);
        const to = byId.get(edge.to);
        if (!from || !to) continue;
        out.push({
          from: { x: from.x, y: from.y },
          to: { x: to.x, y: to.y },
          // The group's label IS the tray type: it is what the estimator
          // renames, what the router resolves against, and what TrayList lists.
          type: group.label,
        });
      }
    }
  }
  return out;
};
